<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMediaQuery } from '@vueuse/core';
import { useStore } from 'vuex';

import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { fetchPdfData, loadPdfjs } from 'dashboard/helper/pdfPreviewHelper';
import MessageReactionBar from 'dashboard/components/widgets/conversation/MessageReactionBar.vue';
import WhatsAppEmojiPicker from 'dashboard/components/widgets/conversation/WhatsAppEmojiPicker.vue';
import MobileEmojiSheet from 'dashboard/components-next/emoji/MobileEmojiSheet.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import PinDurationDialog from 'dashboard/components-next/message/PinDurationDialog.vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { emitter } from 'shared/helpers/mitt';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { isMessagePinned } from 'dashboard/helper/pinnedMessages';
import { useMessageContext } from '../provider.js';

const props = defineProps({
  url: {
    type: String,
    required: true,
  },
  fileName: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();
const isMobileViewport = useMediaQuery('(max-width: 767px)');
const currentAccountId = useMapGetter('getCurrentAccountId');
const conversationGetter = useMapGetter('getConversationById');
const uiSettings = useMapGetter('getUISettings');
const {
  id: messageId,
  conversationId,
  content,
  attachments,
  messageType,
  status,
  contentAttributes,
  private: isPrivate,
  createdAt,
  sourceId,
} = useMessageContext();

const viewerFrame = ref(null);
const pageNumber = ref(1);
const pageCount = ref(0);
const zoom = ref(1);
const isLoading = ref(true);
const loadError = ref(false);
const renderedPages = ref(new Set());
const renderingPages = ref(new Set());
const showReactionMenu = ref(false);
const showFullEmojiPicker = ref(false);
const reactionMenuAnchor = ref(null);
const reactionPickerDirection = ref('down');
const showForwardModal = ref(false);
const pendingMessageAction = ref('');

let pdfDocument;
let loadingTask;
let renderVersion = 0;
let resizeObserver;
let resizeFrame;
let scrollFrame;
const pageCanvases = new Map();
const pageElements = new Map();
const pageRenderTasks = new Map();

const pageLabel = computed(() =>
  t('CONVERSATION.PDF_VIEWER.PAGE_OF', {
    current: pageNumber.value,
    total: pageCount.value,
  })
);
const currentConversation = computed(
  () => conversationGetter.value(conversationId.value) || {}
);
const reactionChannelType = computed(() => {
  const inboxId = currentConversation.value?.inbox_id;
  const getInbox = store.getters['inboxes/getInbox'];
  return inboxId && typeof getInbox === 'function'
    ? getInbox(inboxId)?.channel_type || ''
    : '';
});
const isPinned = computed(() =>
  isMessagePinned(currentConversation.value, messageId.value)
);
const isStarred = computed(() =>
  (uiSettings.value?.starred_message_ids || []).some(
    id => Number(id) === Number(messageId.value)
  )
);
const forwardMessages = computed(() => [
  {
    id: messageId.value,
    content: content.value,
    attachments: attachments.value,
    messageType: messageType.value,
    status: status.value,
    contentAttributes: contentAttributes.value,
    conversationId: conversationId.value,
    private: isPrivate.value,
    createdAt: createdAt.value,
    sourceId: sourceId.value,
  },
]);
const activeMessagePayload = computed(() => ({
  id: messageId.value,
  conversation_id: conversationId.value,
  content: content.value,
  attachments: attachments.value,
  message_type: messageType.value,
  status: status.value,
  content_attributes: contentAttributes.value,
  private: isPrivate.value,
  created_at: createdAt.value,
  source_id: sourceId.value,
}));
const documentPages = computed(() =>
  Array.from({ length: pageCount.value }, (_, index) => index + 1)
);

const cancelCurrentRender = () => {
  renderVersion += 1;
  pageRenderTasks.forEach(renderTask => renderTask.cancel());
  pageRenderTasks.clear();
  renderingPages.value = new Set();
};

const setPageCanvas = page => element => {
  if (element) {
    pageCanvases.set(page, element);
    return;
  }

  pageCanvases.delete(page);
};

const setPageElement = page => element => {
  if (element) {
    pageElements.set(page, element);
    return;
  }

  pageElements.delete(page);
};

const isPageRendering = page => renderingPages.value.has(page);

const setPageRendering = (page, isRendering) => {
  const nextPages = new Set(renderingPages.value);
  if (isRendering) nextPages.add(page);
  else nextPages.delete(page);
  renderingPages.value = nextPages;
};

const renderPage = async targetPage => {
  const targetCanvas = pageCanvases.get(targetPage);
  if (
    !pdfDocument ||
    !targetCanvas ||
    !viewerFrame.value ||
    renderedPages.value.has(targetPage) ||
    renderingPages.value.has(targetPage) ||
    pageRenderTasks.has(targetPage)
  ) {
    return;
  }

  const version = renderVersion;
  setPageRendering(targetPage, true);

  try {
    const page = await pdfDocument.getPage(targetPage);
    if (version !== renderVersion) return;

    const baseViewport = page.getViewport({ scale: 1 });
    const bounds = viewerFrame.value.getBoundingClientRect();
    const availableWidth = Math.max(240, bounds.width - 48);
    const fitScale = Math.min(availableWidth / baseViewport.width, 1.5);
    const viewport = page.getViewport({
      scale: Math.max(0.15, fitScale) * zoom.value,
    });
    const context = targetCanvas.getContext('2d', { alpha: false });

    if (!context || version !== renderVersion) return;

    targetCanvas.width = Math.max(1, Math.floor(viewport.width));
    targetCanvas.height = Math.max(1, Math.floor(viewport.height));
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, targetCanvas.width, targetCanvas.height);

    const renderTask = page.render({ canvasContext: context, viewport });
    pageRenderTasks.set(targetPage, renderTask);
    await renderTask.promise;
    if (version !== renderVersion) return;

    renderedPages.value = new Set([...renderedPages.value, targetPage]);
  } catch (error) {
    if (error?.name !== 'RenderingCancelledException') {
      loadError.value = true;
    }
  } finally {
    pageRenderTasks.delete(targetPage);
    setPageRendering(targetPage, false);
  }
};

const renderVisiblePages = () => {
  if (!viewerFrame.value) return;

  const frameBounds = viewerFrame.value.getBoundingClientRect();
  const renderMargin = frameBounds.height;
  pageElements.forEach((element, page) => {
    const pageBounds = element.getBoundingClientRect();
    if (
      pageBounds.bottom >= frameBounds.top - renderMargin &&
      pageBounds.top <= frameBounds.bottom + renderMargin
    ) {
      renderPage(page);
    }
  });
};

const scheduleVisiblePageRender = () => {
  cancelAnimationFrame(resizeFrame);
  resizeFrame = requestAnimationFrame(renderVisiblePages);
};

const resetRenderedPages = () => {
  cancelCurrentRender();
  renderedPages.value = new Set();
  pageCanvases.forEach(targetCanvas => {
    targetCanvas.width = 1;
    targetCanvas.height = 1;
  });
};

const scrollToPage = (targetPage, behavior = 'smooth') => {
  const targetElement = pageElements.get(targetPage);
  if (!targetElement || !viewerFrame.value) return;

  viewerFrame.value.scrollTo({
    top: Math.max(0, targetElement.offsetTop - 16),
    behavior,
  });
};

const syncActivePageWithScroll = () => {
  if (!viewerFrame.value || !pageElements.size) return;

  const frameBounds = viewerFrame.value.getBoundingClientRect();
  const frameCenter = frameBounds.top + frameBounds.height / 2;
  let closestPage = pageNumber.value;
  let closestDistance = Number.POSITIVE_INFINITY;

  pageElements.forEach((element, page) => {
    const bounds = element.getBoundingClientRect();
    const distance = Math.abs(bounds.top + bounds.height / 2 - frameCenter);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestPage = page;
    }
  });

  if (closestPage !== pageNumber.value) {
    pageNumber.value = closestPage;
  }
};

const handleViewerScroll = () => {
  cancelAnimationFrame(scrollFrame);
  scrollFrame = requestAnimationFrame(() => {
    syncActivePageWithScroll();
    renderVisiblePages();
  });
};

const loadDocument = async () => {
  resetRenderedPages();
  isLoading.value = true;
  loadError.value = false;
  pageNumber.value = 1;
  pageCount.value = 0;

  try {
    const [pdfjs, fileData] = await Promise.all([
      loadPdfjs(),
      fetchPdfData(props.url),
    ]);
    loadingTask = pdfjs.getDocument({ data: new Uint8Array(fileData) });
    pdfDocument = await loadingTask.promise;
    loadingTask = null;
    pageCount.value = pdfDocument.numPages;
    isLoading.value = false;
    await nextTick();
    scrollToPage(1, 'auto');
    renderVisiblePages();
  } catch (error) {
    loadError.value = true;
    isLoading.value = false;
  }
};

const goToPage = async targetPage => {
  const nextPage = Number(targetPage);
  if (nextPage < 1 || nextPage > pageCount.value) return;

  pageNumber.value = nextPage;
  await nextTick();
  scrollToPage(nextPage);
  renderPage(nextPage);
  renderVisiblePages();
};

const changePage = async increment => {
  await goToPage(pageNumber.value + increment);
};

const changeZoom = async increment => {
  const nextZoom = Math.min(2.5, Math.max(0.75, zoom.value + increment));
  if (nextZoom === zoom.value) return;

  zoom.value = Number(nextZoom.toFixed(2));
  resetRenderedPages();
  await nextTick();
  renderVisiblePages();
};

const disposeDocument = async () => {
  cancelCurrentRender();
  await loadingTask?.destroy();
  loadingTask = null;

  if (pdfDocument) {
    const documentToDestroy = pdfDocument;
    pdfDocument = null;
    await documentToDestroy.destroy();
  }
};

const retryLoading = async () => {
  await disposeDocument();
  await loadDocument();
};

const close = () => emit('close');

const goToMessage = () => {
  close();
  window.setTimeout(() => {
    document.getElementById(`message${messageId.value}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, 0);
};

const replyToMessage = () => {
  if (!messageId.value || !conversationId.value) return;

  LocalStorage.updateJsonStore(
    LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO,
    conversationId.value,
    messageId.value
  );
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, activeMessagePayload.value);
  close();
};

const toggleStar = async () => {
  if (!messageId.value || pendingMessageAction.value) return;

  const wasStarred = isStarred.value;
  try {
    pendingMessageAction.value = 'star';
    await store.dispatch('starMessage', {
      accountId: currentAccountId.value,
      messageId: messageId.value,
    });
    useAlert(
      t(
        wasStarred
          ? 'CONVERSATION.PDF_VIEWER.UNSTARRED'
          : 'CONVERSATION.PDF_VIEWER.STARRED'
      )
    );
  } finally {
    pendingMessageAction.value = '';
  }
};

const pinDurationDialogRef = ref(null);

const togglePin = async () => {
  if (!messageId.value || pendingMessageAction.value) return;

  const wasPinned = isPinned.value;
  // Fixar passa antes pelo modal de período, como no WhatsApp; desafixar não,
  // porque o endpoint alterna e não há prazo a escolher.
  let duration = null;
  if (!wasPinned) {
    duration = await pinDurationDialogRef.value?.prompt();
    if (!duration) return;
  }
  try {
    pendingMessageAction.value = 'pin';
    await store.dispatch('pinMessage', {
      accountId: currentAccountId.value,
      conversationId: conversationId.value,
      messageId: messageId.value,
      duration,
    });
    useAlert(
      t(
        wasPinned
          ? 'CONVERSATION.PDF_VIEWER.UNPINNED'
          : 'CONVERSATION.PDF_VIEWER.PINNED'
      )
    );
  } finally {
    pendingMessageAction.value = '';
  }
};

const reactToMessage = async emoji => {
  await store.dispatch('reactToMessage', {
    accountId: currentAccountId.value,
    messageId: messageId.value,
    emoji,
  });
  showReactionMenu.value = false;
  showFullEmojiPicker.value = false;
  useAlert(t('CONVERSATION.PDF_VIEWER.REACTION_ADDED'));
};

const closeReactionMenu = () => {
  showReactionMenu.value = false;
  showFullEmojiPicker.value = false;
};

const toggleReactionMenu = () => {
  if (showReactionMenu.value) {
    closeReactionMenu();
    return;
  }

  showFullEmojiPicker.value = false;
  emitter.emit(BUS_EVENTS.EMOJI_PICKER_OPENED, {
    owner: 'pdf',
    messageId: messageId.value,
  });
  const rect = reactionMenuAnchor.value?.getBoundingClientRect();
  const pickerHeight = Math.min(420, window.innerHeight - 16);
  const fitsBelow = !rect || window.innerHeight - rect.bottom >= pickerHeight;
  const fitsAbove = rect && rect.top >= pickerHeight;
  reactionPickerDirection.value = !fitsBelow && fitsAbove ? 'up' : 'down';
  showReactionMenu.value = true;
};

const openForwardModal = () => {
  closeReactionMenu();
  showForwardModal.value = true;
};

const closeForwardModal = () => {
  showForwardModal.value = false;
};

const downloadDocument = () => {
  const link = document.createElement('a');
  link.href = props.url;
  link.download = props.fileName;
  link.rel = 'noreferrer noopener nofollow';
  link.click();
};

const handleKeydown = event => {
  if (event.key === 'Escape') {
    if (showReactionMenu.value) closeReactionMenu();
    else close();
  }
  if (event.key === 'ArrowUp') changePage(-1);
  if (event.key === 'ArrowDown') changePage(1);
};

const onEmojiPickerOpened = ({ owner, messageId: openedMessageId } = {}) => {
  const isCurrentPicker =
    owner === 'pdf' && Number(openedMessageId) === Number(messageId.value);
  if (!isCurrentPicker) closeReactionMenu();
};

onMounted(async () => {
  window.addEventListener('keydown', handleKeydown);
  emitter.on(BUS_EVENTS.EMOJI_PICKER_OPENED, onEmojiPickerOpened);
  resizeObserver = new ResizeObserver(() => {
    resetRenderedPages();
    scheduleVisiblePageRender();
  });
  resizeObserver.observe(viewerFrame.value);
  await loadDocument();
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleKeydown);
  emitter.off(BUS_EVENTS.EMOJI_PICKER_OPENED, onEmojiPickerOpened);
  resizeObserver?.disconnect();
  cancelAnimationFrame(resizeFrame);
  cancelAnimationFrame(scrollFrame);
  disposeDocument().catch(() => {});
});
</script>

<template>
  <section
    class="absolute inset-0 z-[2147483500] flex min-h-0 flex-col overflow-hidden bg-white text-n-slate-12"
    role="dialog"
    aria-modal="true"
    :aria-label="fileName"
  >
    <!-- Aceleres 2026-09-09 -- no telemovel as accoes deste visualizador
         apertavam-se todas na mesma linha do nome do ficheiro: sete botoes de
         40px mais o voltar disputavam o que sobrava depois do titulo, e a
         soma (8 x 40 = 320px) nao cabia ao lado dele num ecra de 390.

         A partir de `max-md` o cabecalho quebra em duas faixas: em cima o
         voltar ancorado a esquerda (por isso o `pl-14` do titulo, que lhe
         reserva a coluna) com o nome e a contagem de paginas ao lado; em
         baixo a barra de accoes com a largura toda, cada botao a receber uma
         fatia igual (`flex-1`) e 48px de altura -- a mesma area de toque que
         o resto do telemovel ja usa. Assim nada fica apertado, nada sai da
         faixa e o espaco que o botao de eliminar deixou vago e redistribuido
         pelos que ficaram, em vez de virar folga solta a direita.

         O computador nao muda: todas as classes novas estao atras de
         `max-md:`, e a partir de 768px o cabecalho continua a ser a linha
         unica de 4.25rem que sempre foi. -->
    <header
      class="relative flex h-[4.25rem] flex-shrink-0 items-center gap-4 bg-n-background px-4 py-2 max-md:h-auto max-md:flex-wrap max-md:gap-0 max-md:px-2 max-md:py-1.5 sm:px-6"
    >
      <div
        class="min-w-0 flex-1 max-md:flex max-md:min-h-12 max-md:w-full max-md:flex-none max-md:flex-col max-md:justify-center max-md:pl-14 max-md:pr-2"
      >
        <p
          class="truncate text-sm font-semibold text-n-slate-12 sm:text-base"
          :title="fileName"
        >
          {{ fileName }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ pageCount ? pageLabel : $t('CONVERSATION.PDF_VIEWER.LOADING') }}
        </p>
      </div>

      <div
        class="gallery-toolbar ml-2 flex flex-shrink-0 items-center gap-0.5 max-md:ml-0 max-md:w-full max-md:flex-none max-md:justify-between max-md:gap-1 sm:gap-1"
      >
        <NextButton
          icon="i-lucide-message-square-text"
          slate
          ghost
          class="gallery-action-show-chat !rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-6 [&>span]:!size-5"
          :disabled="!messageId"
          :aria-label="$t('CONVERSATION.PDF_VIEWER.GO_TO_MESSAGE')"
          :title="$t('CONVERSATION.PDF_VIEWER.GO_TO_MESSAGE')"
          @click="goToMessage"
        />
        <NextButton
          icon="i-lucide-reply"
          slate
          ghost
          class="gallery-action-reply !rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-[26px] [&>span]:!size-[22px]"
          :disabled="!messageId"
          :aria-label="$t('CONVERSATION.REPLYBOX.REPLY')"
          :title="$t('CONVERSATION.REPLYBOX.REPLY')"
          @click="replyToMessage"
        />
        <NextButton
          icon="i-lucide-star"
          slate
          ghost
          class="gallery-action-star !rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-6 [&>span]:!size-5"
          :class="{ '!text-[#00a884] [&>span]:fill-current': isStarred }"
          :is-loading="pendingMessageAction === 'star'"
          :disabled="!messageId || Boolean(pendingMessageAction)"
          :aria-label="
            $t(
              isStarred
                ? 'CONVERSATION.CONTEXT_MENU.UNSTAR'
                : 'CONVERSATION.CONTEXT_MENU.STAR'
            )
          "
          :title="
            $t(
              isStarred
                ? 'CONVERSATION.CONTEXT_MENU.UNSTAR'
                : 'CONVERSATION.CONTEXT_MENU.STAR'
            )
          "
          @click="toggleStar"
        />
        <NextButton
          icon="i-lucide-pin"
          slate
          ghost
          class="gallery-action-pin !rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-6 [&>span]:!size-5"
          :class="{ '!text-[#00a884] [&>span]:fill-current': isPinned }"
          :is-loading="pendingMessageAction === 'pin'"
          :disabled="!messageId || Boolean(pendingMessageAction)"
          :aria-label="
            $t(
              isPinned
                ? 'CONVERSATION.CONTEXT_MENU.UNPIN'
                : 'CONVERSATION.CONTEXT_MENU.PIN'
            )
          "
          :title="
            $t(
              isPinned
                ? 'CONVERSATION.CONTEXT_MENU.UNPIN'
                : 'CONVERSATION.CONTEXT_MENU.PIN'
            )
          "
          @click="togglePin"
        />
        <div
          ref="reactionMenuAnchor"
          class="relative flex flex-shrink-0 max-md:static max-md:flex-1"
        >
          <NextButton
            icon="i-lucide-smile-plus"
            slate
            ghost
            class="!rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-6 [&>span]:!size-5"
            :class="{ '!bg-n-alpha-2': showReactionMenu }"
            :disabled="!messageId"
            :aria-label="$t('CONVERSATION.PDF_VIEWER.REACT')"
            :title="$t('CONVERSATION.PDF_VIEWER.REACT')"
            @click="toggleReactionMenu"
          />
          <div
            v-if="showReactionMenu && !(isMobileViewport && showFullEmojiPicker)"
            :key="showFullEmojiPicker ? 'emojis' : 'reacoes'"
            class="absolute right-0 z-20 overflow-hidden border border-n-weak bg-n-solid-1 shadow-lg max-md:origin-top-right max-md:right-2 max-md:!top-[calc(100%+0.25rem)] max-md:!bottom-auto"
            :class="[
              reactionPickerDirection === 'up'
                ? 'bottom-[calc(100%+0.5rem)]'
                : 'top-[calc(100%+0.5rem)]',
              showFullEmojiPicker
                ? 'rounded-xl animate-[fade-in-up_0.16s_ease-out_both]'
                : 'rounded-full px-1.5 animate-reaction-pop-in',
            ]"
          >
            <MessageReactionBar
              v-if="!showFullEmojiPicker"
              :channel-type="reactionChannelType"
              large
              @react="reactToMessage"
              @more="showFullEmojiPicker = true"
              @close="closeReactionMenu"
            />
            <WhatsAppEmojiPicker
              v-else
              embedded
              show-recent-reactions
              v-on-clickaway="closeReactionMenu"
              :on-select="reactToMessage"
            />
          </div>
          <!-- Aceleres 2026-09-15 (Facundo): no telemovel o seletor grande e a
               folha unica de emojis, por cima do visor. -->
          <MobileEmojiSheet
            :open="isMobileViewport && showReactionMenu && showFullEmojiPicker"
            :on-select="reactToMessage"
            @close="closeReactionMenu"
          />
        </div>
        <NextButton
          :icon="
            isMobileViewport
              ? 'i-lucide-forward'
              : 'i-tabler-corner-up-right-double'
          "
          slate
          ghost
          class="gallery-action-forward !rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-6 [&>span]:!size-5 max-md:[&>svg]:!size-5 [&>svg]:!size-[18px]"
          :disabled="!messageId"
          :aria-label="$t('CONVERSATION.PDF_VIEWER.FORWARD')"
          :title="$t('CONVERSATION.PDF_VIEWER.FORWARD')"
          @click="openForwardModal"
        />
        <NextButton
          icon="i-lucide-download"
          slate
          ghost
          class="gallery-action-download !rounded-full max-md:!h-12 max-md:flex-1 max-md:[&>span]:!size-6 [&>span]:!size-5"
          :aria-label="$t('CONVERSATION.DOWNLOAD')"
          :title="$t('CONVERSATION.DOWNLOAD')"
          @click="downloadDocument"
        />
        <NextButton
          icon="i-lucide-arrow-left"
          slate
          ghost
          class="gallery-viewer-close flex-shrink-0 !rounded-full [&>span]:!size-6 max-md:absolute max-md:left-1 max-md:top-1.5 max-md:!size-12 max-md:flex-none"
          :aria-label="$t('GENERAL.CLOSE')"
          :title="$t('GENERAL.CLOSE')"
          @click="close"
        />
      </div>
    </header>

    <div class="relative flex min-h-0 flex-1 overflow-hidden bg-white">
      <div
        ref="viewerFrame"
        class="relative min-w-0 flex-1 overflow-auto bg-n-slate-2 p-3 sm:p-8"
        @scroll="handleViewerScroll"
      >
        <div
          v-if="isLoading"
          class="flex flex-col items-center gap-3 text-sm text-n-slate-10"
        >
          <i class="i-lucide-loader-circle size-7 animate-spin" />
          <span>{{ $t('CONVERSATION.PDF_VIEWER.LOADING') }}</span>
        </div>

        <div
          v-else-if="loadError"
          class="flex max-w-sm flex-col items-center gap-3 rounded-xl border border-n-slate-3 bg-white px-6 py-5 text-center shadow-sm"
        >
          <i class="i-lucide-file-warning size-7 text-n-slate-10" />
          <p class="text-sm text-n-slate-11">
            {{ $t('CONVERSATION.PDF_VIEWER.LOAD_ERROR') }}
          </p>
          <button
            type="button"
            class="rounded-lg bg-[#00a884] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
            @click="retryLoading"
          >
            {{ $t('CONVERSATION.PDF_VIEWER.RETRY') }}
          </button>
        </div>

        <div
          v-else
          class="mx-auto flex min-h-full w-full max-w-[80rem] flex-col items-start gap-6 py-3"
        >
          <article
            v-for="page in documentPages"
            :key="page"
            :ref="setPageElement(page)"
            class="relative mx-auto flex min-h-[24rem] w-max min-w-full shrink-0 items-center justify-center"
          >
            <canvas
              :ref="setPageCanvas(page)"
              :aria-label="
                $t('CONVERSATION.PDF_VIEWER.PAGE_OF', {
                  current: page,
                  total: pageCount,
                })
              "
              class="block bg-white shadow-2xl"
            />
            <div
              v-if="isPageRendering(page)"
              class="absolute inset-0 flex items-center justify-center text-n-slate-10"
              aria-live="polite"
            >
              <i class="i-lucide-loader-circle size-6 animate-spin" />
            </div>
          </article>
        </div>
      </div>

      <aside
        v-if="pageCount && !isLoading && !loadError"
        class="absolute bottom-4 right-4 z-10 flex w-12 flex-col items-center overflow-hidden rounded-2xl border border-n-slate-3 bg-white py-1 shadow-xl sm:bottom-6 sm:right-6"
        :aria-label="
          $t('CONVERSATION.PDF_VIEWER.PAGE_OF', {
            current: pageNumber,
            total: pageCount,
          })
        "
      >
        <div class="flex flex-col items-center gap-0.5 px-1 py-2">
          <div
            class="flex size-10 items-center justify-center rounded-xl border border-n-slate-3 bg-white text-sm font-medium text-n-slate-12"
            :aria-label="
              $t('CONVERSATION.PDF_VIEWER.PAGE_OF', {
                current: pageNumber,
                total: pageCount,
              })
            "
          >
            {{ pageNumber }}
          </div>
          <span class="text-xs text-n-slate-10">{{ pageCount }}</span>
        </div>

        <div class="h-px w-7 bg-n-slate-3" />

        <button
          type="button"
          class="inline-flex size-10 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-slate-3 disabled:cursor-not-allowed disabled:opacity-30"
          :disabled="pageNumber <= 1"
          :aria-label="$t('CONVERSATION.PDF_VIEWER.PREVIOUS_PAGE')"
          :title="$t('CONVERSATION.PDF_VIEWER.PREVIOUS_PAGE')"
          @click="changePage(-1)"
        >
          <i class="i-lucide-chevron-up size-5" />
        </button>
        <button
          type="button"
          class="inline-flex size-10 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-slate-3 disabled:cursor-not-allowed disabled:opacity-30"
          :disabled="pageNumber >= pageCount"
          :aria-label="$t('CONVERSATION.PDF_VIEWER.NEXT_PAGE')"
          :title="$t('CONVERSATION.PDF_VIEWER.NEXT_PAGE')"
          @click="changePage(1)"
        >
          <i class="i-lucide-chevron-down size-5" />
        </button>

        <div class="h-px w-7 bg-n-slate-3" />

        <button
          type="button"
          class="inline-flex size-10 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-slate-3 disabled:cursor-not-allowed disabled:opacity-30"
          :disabled="zoom <= 0.75"
          :aria-label="$t('CONVERSATION.PDF_VIEWER.ZOOM_OUT')"
          :title="$t('CONVERSATION.PDF_VIEWER.ZOOM_OUT')"
          @click="changeZoom(-0.25)"
        >
          <i class="i-lucide-zoom-out size-5" />
        </button>
        <button
          type="button"
          class="inline-flex size-10 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-slate-3 disabled:cursor-not-allowed disabled:opacity-30"
          :disabled="zoom >= 2.5"
          :aria-label="$t('CONVERSATION.PDF_VIEWER.ZOOM_IN')"
          :title="$t('CONVERSATION.PDF_VIEWER.ZOOM_IN')"
          @click="changeZoom(0.25)"
        >
          <i class="i-lucide-zoom-in size-5" />
        </button>
      </aside>
    </div>

    <!-- Período de fixação (Aceleres): abre antes de fixar, nunca ao desafixar. -->
    <PinDurationDialog ref="pinDurationDialogRef" />

    <ForwardMessageModal
      :show="showForwardModal"
      :conversation-id="conversationId"
      :selected-messages="forwardMessages"
      @cancel="closeForwardModal"
      @success="closeForwardModal"
      @update:show="showForwardModal = $event"
    />
  </section>
</template>
